/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { makeStyles, tokens } from '@fluentui/react-components';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROLES } from '../../constants/roles';
import { type AdminRouterItem, routes } from '../../router';

// Define styles for the sidebar
const useStyles = makeStyles({
  sider: {
    backgroundColor: tokens.colorNeutralBackground1,
    width: '220px',
    minWidth: '220px',
    maxWidth: '220px',
    height: '100vh',
    overflowY: 'auto',
    padding: tokens.spacingVerticalM,
    transition: 'transform 0.3s ease',
    flexShrink: 0,

    '@media (max-width: 768px)': {
      position: 'fixed',
      top: 0,
      left: 0,
      bottom: 0,
      zIndex: 1000,
      transform: 'translateX(-100%)',
      width: '250px',
      minWidth: '250px',
      maxWidth: '250px',
      padding: tokens.spacingVerticalM,
      boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
    },
  },
  siderOpen: {
    '@media (max-width: 768px)': {
      transform: 'translateX(0)',
    },
  },
  navItem: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    minHeight: '44px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    '@media (max-width: 768px)': {
      minHeight: '48px',
      padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalM}`,
    },
  },
  navItemText: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase400,
  },
  selectedNavItem: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  nestedNavItem: {
    paddingLeft: tokens.spacingHorizontalL,

    '@media (max-width: 768px)': {
      paddingLeft: tokens.spacingHorizontalS,
    },
  },
  navItemIcon: {
    fontSize: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  navItemContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: tokens.spacingHorizontalS,
  },
});

const ROLE_STORAGE_KEY = 'userPersonalRole';

const getCurrentRole = (): string[] => {
  try {
    const raw = sessionStorage.getItem(ROLE_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw) as string[];

    if (Array.isArray(parsed)) {
      return parsed.map((r) => r.trim());
    }

    return [];
  } catch (error) {
    return [];
  }
};

const roleMatches = (
  allowed?: typeof ROLES,
  currentRoles?: string[]
): boolean => {
  if (!allowed || allowed.length === 0) {
    return true;
  }

  if (!currentRoles || currentRoles.length === 0) {
    return false;
  }

  // Case-insensitive comparison
  const allowedLower = allowed.map((r) => r.toLowerCase().trim());
  const currentRolesLower = currentRoles.map((r) => r.toLowerCase().trim());
  return currentRolesLower.some((role) => allowedLower.includes(role));
};

const getMenuItems = (
  routes: AdminRouterItem[],
  parentPath = '',
  currentRoles?: string[],
  isInProjectView = false
): any[] => {
  return routes
    .map((itm) => {
      if (!itm.meta) {
        return null;
      }

      if (itm.meta.hideInMenu) {
        return null;
      }

      if (!roleMatches(itm.meta.allowedRoles, currentRoles)) {
        return null;
      }

      // Filter routes based on current view context
      const isProjectRoute = itm.path === 'project' || itm.meta.key === '/project' || itm.meta.key?.startsWith('/project');
      if (isInProjectView && !isProjectRoute) {
        return null; // Hide dashboard routes when in project view
      }
      if (!isInProjectView && isProjectRoute) {
        return null; // Hide project routes when not in project view
      }

      const fullPath = parentPath + itm.path;
      const children = itm.children
        ? getMenuItems(itm.children, fullPath + '/', currentRoles, isInProjectView)
        : null;

      return {
        ...itm.meta,
        content: itm.meta.label,
        key: itm.meta.key,
        path: fullPath,
        items: children,
      };
    })
    .filter((itm) => !!itm);
};

interface PageSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const PageSidebar: React.FC<PageSidebarProps> = ({
  isOpen = false,
  onClose,
}) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>(getCurrentRole());

  useEffect(() => {
    const handleRoleChange = () => {
      const newRoles = getCurrentRole();
      setRoles(newRoles);
    };

    window.addEventListener('role-changed', handleRoleChange);
    window.addEventListener('storage', handleRoleChange);

    return () => {
      window.removeEventListener('role-changed', handleRoleChange);
      window.removeEventListener('storage', handleRoleChange);
    };
  }, []);

  const menuItems = useMemo(() => {
    const isInProjectView = location.pathname.startsWith('/project');
    const items = getMenuItems(routes, '', roles, isInProjectView);
    return items;
  }, [roles, location.pathname]);

  const onSwitchMenu = (key: string) => {
    navigate(key);
  };

  useEffect(() => {
    setSelectedKeys([location.pathname]);
  }, [location.pathname]);

  const renderMenuItems = (items: any[], depth = 0) => {
    return items.map((item) => (
      <div key={item.key}>
        <div
          className={`${styles.navItem} ${selectedKeys.includes(item.key) ? styles.selectedNavItem : ''} ${depth > 0 ? styles.nestedNavItem : ''}`}
          onClick={() => {
            onSwitchMenu(item.key);
            if (window.innerWidth < 768 && onClose) {
              onClose();
            }
          }}
          title={item.content}
        >
          <div className={styles.navItemContainer}>
            {item.icon && (
              <span className={styles.navItemIcon}>{item.icon}</span>
            )}
            <span className={styles.navItemText}>{item.content}</span>
          </div>
        </div>
        {item.items && item.items.length > 0 && (
          <div className={styles.nestedNavItem}>
            {renderMenuItems(item.items, depth + 1)}
          </div>
        )}
      </div>
    ));
  };

  // Hide main sidebar when in project view
  const isInProjectView = location.pathname.startsWith('/project');
  if (isInProjectView) {
    return null;
  }

  return (
    <div className={`${styles.sider} ${isOpen ? styles.siderOpen : ''}`}>
      {renderMenuItems(menuItems)}
    </div>
  );
};

export default PageSidebar;
